// src/hooks/orderbooks/useBinanceSpot.js
import { useEffect, useRef, useState } from "react";

const DEFAULT_PROXY = "";
const REST_BASE = "https://api.binance.com";
const WS_BASE   = "wss://stream.binance.com:9443/ws";

/**
 * Binance SPOT orderbook hook:
 * - 1) буферим WS diff depth события
 * - 2) тянем REST snapshot (depth)
 * - 3) отбрасываем устаревшие события, применяем diff по процедуре из доков
 * - 4) дальше применяем все входящие события, проверяя непрерывность (U === lastU+1)
 *
 * Возвращает: { isConnected, error, orderBook:{asks,bids}, tickerData }
 * tickerData берём из miniTicker (опционально), чтобы получить lastPrice/volume.
 */
export function useBinanceSpot(symbol, {
  limit = 100,                 // 1..5000
  proxy = DEFAULT_PROXY,       // REST через прокси (CORS)
  withTicker = true,           // подписываться на <symbol>@miniTicker
  topN = 20                    // сколько уровней держать в состоянии для UI
} = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const [tickerData, setTickerData] = useState(null);


  const wsRef       = useRef(null);
  const bufferRef   = useRef([]);     // очередь diff событий до снапшота
  const hasSnapRef  = useRef(false);
  const lastIdRef   = useRef(null);   // lastUpdateId локального стакана
  const bookRef     = useRef({ asks: [], bids: [] });
  const rafRef      = useRef(null);

  // --- helpers
  const normSym = (s) => String(s || "").replace(/[-_]/g, "").toUpperCase();
  const asNum = (x) => Number.parseFloat(x);

  const setBookState = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      // сортируем и режем
      const asks = [...bookRef.current.asks]
        .sort((a, b) => asNum(a[0]) - asNum(b[0]))
        .slice(0, topN);

      const bids = [...bookRef.current.bids]
        .sort((a, b) => asNum(b[0]) - asNum(a[0]))
        .slice(0, topN);

      setOrderBook({ asks, bids });
    });
  };

  const applyLevels = (levels, side) => {
    const base = bookRef.current[side] || [];
    // применяем «upsert/remove»
    for (const [priceStr, qtyStr] of levels) {
      const p = asNum(priceStr);
      const q = asNum(qtyStr);
      const idx = base.findIndex(([pp]) => asNum(pp) === p);
      if (q === 0) {
        if (idx !== -1) base.splice(idx, 1);
      } else {
        if (idx !== -1) base[idx] = [priceStr, qtyStr];
        else base.push([priceStr, qtyStr]);
      }
    }
    bookRef.current[side] = base;
  };

  const applyEvent = (evt) => {
    // проверка непрерывности
    const lastU = lastIdRef.current;
    const U = evt.U, u = evt.u;

    if (lastU != null) {
      if (u < lastU) {
        // устаревшее — игнор
        return false;
      }
      if (U > lastU + 1) {
        // разрыв — нужно полная ресинхронизация
        throw new Error("GAP_IN_BUFFER");
      }
    }

    // применяем
    applyLevels(evt.b || [], "bids");
    applyLevels(evt.a || [], "asks");

    lastIdRef.current = u;
    return true;
  };

  const fetchSnapshot = async (sym) => {
    const restUrl = `${REST_BASE}/api/v3/depth?symbol=${sym}&limit=${limit}`;
    const url = proxy ? `${proxy}/${restUrl}` : restUrl;

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Binance depth HTTP ${res.status}`);
    const js = await res.json();

    const { lastUpdateId, bids, asks } = js || {};
    if (!lastUpdateId || !Array.isArray(bids) || !Array.isArray(asks)) {
      throw new Error("Bad snapshot payload");
    }

    // ставим снапшот локально
    bookRef.current = { bids: [...bids], asks: [...asks] };
    lastIdRef.current = Number(lastUpdateId);
    hasSnapRef.current = true;
    setBookState();

    // отфильтруем буфер (u <= lastUpdateId выбрасываем),
    // ищем первое событие с U <= lastUpdateId+1 <= u
    // применяем оставшиеся по порядку
    const kept = [];
    for (const e of bufferRef.current) {
      if (e.u <= lastIdRef.current) continue;
      kept.push(e);
    }
    bufferRef.current = kept;

    if (bufferRef.current.length) {
      const first = bufferRef.current[0];
      const okFirst =
        first.U <= lastIdRef.current + 1 &&
        first.u >= lastIdRef.current + 1;

      if (!okFirst) {
        // снапшот не попал в диапазон — нужно переснять снова
        throw new Error("GAP_IN_BUFFER");
      }

      // применим все оставшиеся
      for (const e of bufferRef.current) applyEvent(e);
      bufferRef.current = [];
      setBookState();
    }
  };

  useEffect(() => {
    const sym = normSym(symbol);
    if (!sym) return;

    setIsConnected(false);
    setError(null);
    setOrderBook({ asks: [], bids: [] });
    setTickerData(null);

    // сброс локальных структур
    bufferRef.current = [];
    hasSnapRef.current = false;
    lastIdRef.current = null;
    bookRef.current = { asks: [], bids: [] };

    const streams = [ `${sym.toLowerCase()}@depth@100ms` ];
    if (withTicker) streams.push(`${sym.toLowerCase()}@miniTicker`);

    const ws = new WebSocket(`${WS_BASE}/${streams.length === 1 ? streams[0] : streams.join("/")}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
    };

    ws.onerror = (e) => {
      setError(e?.message || "WS error");
      setIsConnected(false);
    };

    ws.onclose = () => {
      setIsConnected(false);
    };

    ws.onmessage = async (ev) => {
      try {
        const m = JSON.parse(ev.data);

        // combined stream имеет {stream, data}
        const data = m.data || m;

        // miniTicker
        if (data?.e === "24hrMiniTicker") {
          setTickerData({
            symbol: data.s,
            lastPrice: data.c,
            volume: data.q,
            volume24: data.q,
            bid: orderBook?.bids?.[0]?.[0] || null,
            ask: orderBook?.asks?.[0]?.[0] || null,
          });
          return;
        }

        // depthUpdate
        if (data?.e === "depthUpdate") {
          const evt = {
            U: Number(data.U),
            u: Number(data.u),
            b: data.b || [],
            a: data.a || [],
          };

          if (!hasSnapRef.current) {
            // до снапшота — буферим
            bufferRef.current.push(evt);
            // если первое событие, сразу тянем снапшот и попробуем склеить
            if (bufferRef.current.length === 1) {
              try {
                await fetchSnapshot(sym);
              } catch (e) {
                // сброс и заново
                bufferRef.current = [];
                hasSnapRef.current = false;
                lastIdRef.current = null;
                bookRef.current = { asks: [], bids: [] };
              }
            }
            return;
          }

          // после снапшота — применяем по правилам
          try {
            const applied = applyEvent(evt);
            if (applied) setBookState();
          } catch (e) {
            if (String(e?.message).includes("GAP_IN_BUFFER")) {
              // переснять снапшот: очистим буфер и снова загрузим
              bufferRef.current = [];
              hasSnapRef.current = false;
              lastIdRef.current = null;
              bookRef.current = { asks: [], bids: [] };
              try {
                await fetchSnapshot(sym);
              } catch (e2) {
              }
            }
          }
        }
      } catch {}
    };

    return () => {
      try { ws.close(); } catch {}
      wsRef.current = null;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, limit, proxy, withTicker, topN]);

  return { isConnected, error, orderBook, tickerData };
}

export default useBinanceSpot;
